'use client';

import { useState } from 'react';
import { ServiceLocation } from './service-location';
import { ServiceList } from './service-list';
import { ServiceCart } from './service-cart';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Clock, ShoppingCart } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  CartItem,
  ServiceResource,
  ServicePrice,
  Service,
} from '@/types/service';
import { PageHeader } from '../components/page-header';
import { Page } from '../../types/page';
import { Booking } from '../../types/booking';
import { ServiceSlots } from './service-slots';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { BookingSuccess } from '../components/booking-success';
import { BookingForm } from '../components/booking-form';
import { toast } from 'sonner';
import { ApiEndpoint, apiService } from '../../lib/api';
import { convertToMinutes } from '../../helper/utility';

interface ServiceBookingProps {
  page: Page;
  resource: ServiceResource;
  onDone: () => void;
}

export function ServiceBooking({
  page,
  resource,
  onDone,
}: ServiceBookingProps) {
  const [currentStep, setCurrentStep] = useState<
    'services' | 'slots' | 'form' | 'success'
  >('services');
  const [selectedLocation, setSelectedLocation] = useState(
    resource.locations[0]
  );
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [booking, setBooking] = useState<Booking>({} as Booking);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [showCart, setShowCart] = useState(false);

  const handleServiceSelection = (services: any[]) => {
    setCartItems(services);
    const totalDuration = services.reduce(
      (total, item) => total + item.price.duration,
      0
    );
    setBooking({
      ...booking,
      resource_id: resource.resource_id,
      location: resource.locations[0],
      duration: totalDuration,
    });
    setCurrentStep('slots');
  };

  const handleAddToCart = (service: Service, price: ServicePrice) => {
    setCartItems([
      ...cartItems,
      {
        service,
        price: { ...price, duration: convertToMinutes(price.duration) },
      },
    ]);
  };

  const handleRemoveFromCart = (index: number) => {
    const newCart = cartItems.filter((_, i) => i !== index);
    setCartItems(newCart);

    if (!newCart.length && currentStep !== 'services') {
      setCurrentStep('services');
    }
  };

  const handleCartUpdate = (updatedItems: CartItem[]) => {
    setCartItems(updatedItems);
  };

  const getTotalDuration = () => {
    return cartItems.reduce((total, item) => total + item.price.duration, 0);
  };

  const getTotalPrice = () => {
    return cartItems.reduce(
      (total, item) => total + (item.price.amount || 0),
      0
    );
  };

  const handleBack = () => {
    switch (currentStep) {
      case 'services':
        onDone();
        break;
      case 'slots':
        setCurrentStep('services');
        break;
      default:
        break;
    }
  };

  const handleNext = () => {
    if (currentStep === 'services') {
      handleServiceSelection(cartItems);
    } else {
      setShowBookingDialog(true);
    }
  };

  const handleBookingSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      for (const item of cartItems) {
        const { service, price, slot, staff } = item;
        if (!slot) return;
        const finalBooking: Booking = {
          resource: {
            resource_id: resource.resource_id,
            name: resource.name,
            type: resource.type,
            service_id: service.id,
          },
          host: staff?.id
            ? {
                user_id: parseInt(staff?.id),
              }
            : undefined,
          start_at: slot.start_at,
          end_at: slot.end_at,
          duration: slot.duration,
          booking_url: `https://${page.domain}/${resource.slug}`,
          resource_id: resource.resource_id,
          time_zone: slot.time_zone,
          ...data,
        };
        if (finalBooking.invitees?.length) {
          finalBooking.invitees = finalBooking.invitees.map((invitee) => ({
            ...invitee,
            time_zone: slot.time_zone,
            payment:
              price?.amount > 0
                ? {
                    ...invitee.payment,
                    price: price.amount,
                    currency: price.currency,
                  }
                : undefined,
          }));
        }
        const response = await apiService.post(
          ApiEndpoint.bookings,
          finalBooking
        );
        setBookings((prev) => [...prev, response]);
      }
      setCurrentStep('success');
    } catch (err: any) {
      toast.error(err.message || 'Something went wrong, please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="mx-auto px-4 py-6">
        <div className="mb-6">
          <Button variant="ghost" onClick={handleBack} className="mb-3">
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <PageHeader
            logo={page.logo}
            name={page.name}
            description={page.description}
          />
        </div>

        {/* Location Selection */}
        {resource.locations.length > 1 ? (
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-6">Select a Location</h2>
            <ServiceLocation
              locations={resource.locations}
              selectedLocation={selectedLocation}
              onSelect={setSelectedLocation}
            />
          </div>
        ) : null}

        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-6">
            {currentStep === 'services'
              ? 'Select Services'
              : ' Select Date & Time'}{' '}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">
            {/* Services List */}

            {currentStep === 'services' && (
              <ServiceList
                services={resource.services}
                cartItems={cartItems}
                onAddToCart={handleAddToCart}
              />
            )}

            {currentStep === 'slots' && (
              <ServiceSlots
                resource={resource}
                selectedServices={cartItems}
                onCartUpdate={handleCartUpdate}
              />
            )}

            {/* Cart - Desktop */}
            <div className="hidden lg:block">
              <div className="sticky top-6">
                <ServiceCart
                  items={cartItems}
                  location={selectedLocation}
                  onRemove={handleRemoveFromCart}
                  totalDuration={getTotalDuration()}
                  totalPrice={getTotalPrice()}
                  onProceed={handleNext}
                  currentStep={currentStep}
                />
              </div>
            </div>

            {/* Cart - Mobile */}
            <div className="lg:hidden fixed bottom-4 left-4 right-4 z-50">
              <Sheet open={showCart} onOpenChange={setShowCart}>
                <SheetTrigger asChild>
                  <Button className="w-full" size="lg">
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    View Cart ({cartItems.length})
                    <div className="ml-auto flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {getTotalDuration()} min
                    </div>
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-[80vh]">
                  <SheetHeader>
                    <SheetTitle>Your Services</SheetTitle>
                  </SheetHeader>
                  <ServiceCart
                    items={cartItems}
                    location={selectedLocation}
                    onRemove={handleRemoveFromCart}
                    totalDuration={getTotalDuration()}
                    totalPrice={getTotalPrice()}
                    onProceed={() => {
                      setShowCart(false);
                      handleNext();
                    }}
                    currentStep={currentStep}
                  />
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="max-w-xl">
          {currentStep === 'success' ? (
            <BookingSuccess
              resource={resource}
              booking={bookings[0]}
              isDialog={true}
            />
          ) : (
            <BookingForm
              page={page}
              resource={resource}
              onSubmit={handleBookingSubmit}
              onBack={() => {
                setShowBookingDialog(false);
                setBookings([]);
              }}
              isSubmitting={isSubmitting}
              isDialog={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
